import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { FiAlertTriangle, FiCheck, FiCheckCircle, FiClock, FiMail } from "react-icons/fi";
import { Spinner } from "components/ui";
import { EmailFrequency, previewEmailFrequency, updateEmailFrequency } from "./EmailFrequencyPreferences.api";

const labels: Record<EmailFrequency, string> = {
  1: "Daily",
  2: "2 days a week",
  3: "3 days a week",
};

type PageStatus = "loading" | "ready" | "saved" | "invalid" | "error";

const getHomeUrl = () => {
  if (process.env.REACT_APP_ENV === "staging") {
    return "https://yellow-glacier-0b1946010.4.azurestaticapps.net/";
  }

  if (process.env.REACT_APP_ENV === "Production") {
    return "https://zampliasurveys.com/";
  }

  return "/";
};

const EmailFrequencyPreferences = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [frequency, setFrequency] = useState<EmailFrequency>(1);
  const [status, setStatus] = useState<PageStatus>("loading");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    previewEmailFrequency(token)
      .then((response: any) => {
        if (!response?.success || !response?.result?.frequency) {
          setStatus("invalid");
          return;
        }
        setFrequency(response.result.frequency as EmailFrequency);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [token]);

  const mutation = useMutation(
    (selected: EmailFrequency) => updateEmailFrequency(token, selected),
    {
      onSuccess: (response: any) => {
        if (!response?.success) {
          setStatus("error");
          return;
        }

        setStatus("saved");
        window.location.assign(getHomeUrl());
      },
      onError: () => setStatus("error"),
    }
  );

  const selectFrequency = (selected: EmailFrequency) => {
    setFrequency(selected);
    setStatus("ready");
  };

  return (
    <div className="flex w-full items-center justify-center min-h-[calc(100vh-var(--zs-public-header-h,64px))] px-4 py-10 sm:py-16">
      <div className="w-full max-w-lg p-6 zs-card sm:p-8">
        {status === "loading" && (
          <div className="flex flex-col items-center gap-3 py-6 text-sm text-center text-ink-3">
            <Spinner className="w-6 h-6" />
            <p>Checking your link...</p>
          </div>
        )}
        {status === "invalid" && (
          <div className="flex flex-col items-center text-center">
            <span className="zs-empty-icon bg-warning-soft text-warning">
              <FiClock />
            </span>
            <h2 className="mb-2 text-xl font-semibold font-display text-ink">This link has expired</h2>
            <p className="text-sm text-ink-3">Please use a newer email preference link.</p>
          </div>
        )}
        {(status === "ready" || status === "saved") && (
          <>
            <div className="mb-6">
              <span className="zs-eyebrow">
                <FiMail /> Email settings
              </span>
              <h2 className="text-xl font-semibold sm:text-2xl font-display text-ink">Email preferences</h2>
              <p className="mt-1.5 text-sm text-ink-3">How often would you like to receive emails?</p>
            </div>
            <div className="flex flex-col gap-2.5" role="radiogroup" aria-label="Email frequency">
              {([1, 2, 3] as EmailFrequency[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={frequency === value}
                  disabled={mutation.isLoading}
                  onClick={() => selectFrequency(value)}
                  className={`flex w-full items-center justify-between gap-3 rounded border px-4 py-3 text-left text-sm font-medium transition-colors disabled:opacity-50 ${
                    frequency === value
                      ? "border-primary bg-primary-soft text-primary-text ring-1 ring-primary"
                      : "border-line-strong bg-surface text-ink-2 hover:bg-surface-hover"
                  }`}
                >
                  <span>{labels[value]}</span>
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full border shrink-0 ${
                      frequency === value ? "border-primary bg-primary text-ink-inverse" : "border-line-strong"
                    }`}
                    aria-hidden="true"
                  >
                    {frequency === value && <FiCheck className="w-3 h-3" />}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={mutation.isLoading}
              onClick={() => mutation.mutate(frequency)}
              className="mt-6 zs-btn zs-btn-primary zs-btn-lg zs-btn-block"
            >
              {mutation.isLoading ? "Saving..." : "Save Preferences"}
            </button>
            {status === "saved" && (
              <p className="flex items-center gap-2 px-3 py-2 mt-3 text-sm font-medium rounded text-success bg-success-soft">
                <FiCheckCircle className="shrink-0" />
                Preference saved. You will receive these emails {labels[frequency].toLowerCase()}.
              </p>
            )}
            <p className="mt-4 text-xs text-ink-4">
              This changes only how often these emails arrive. It does not unsubscribe you.
            </p>
          </>
        )}
        {status === "error" && (
          <div className="flex flex-col items-center text-center">
            <span className="zs-empty-icon bg-danger-soft text-danger">
              <FiAlertTriangle />
            </span>
            <h2 className="mb-2 text-xl font-semibold font-display text-danger">Something went wrong</h2>
            <p className="text-sm text-ink-3">Please open the email link and try again.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailFrequencyPreferences;
